import {makeAutoObservable, reaction, runInAction} from 'mobx'
import config, {systemObjects} from 'src/config'
import {MainStore} from 'src/store/MainStore'
import Student, {StudentTab} from 'src/entities/Student'
import StudentReport from 'src/entities/StudentReport'
import StudentMonthlyChange from 'src/entities/StudentMonthlyChange'
import Report from 'src/entities/Report'
import CodeObject from 'src/entities/CodeObject'
import {format} from 'date-fns'
import StudentRejection from 'src/entities/StudentRejection'
import Dashboard from '../entities/Dashboard'
import Month from 'src/entities/Month'
import {defaultMonth, getMonthName, getYearText} from 'src/utils/month'

type DashboardMatrix = {
  [year: number]: {
    [month: number]: Dashboard
  }
}

export class StudentsStore {
  students: Student[] = []

  studentReports: StudentReport[] = []

  lastReport?: Report

  isLoadingLastReport: boolean = false

  studentImportStatuses: CodeObject[] = []

  duplicates: Student[] = []

  rejects: StudentRejection[] = []

  studentStatuses?: CodeObject[]

  learningShifts?: CodeObject[]

  selectedInstitutionId?: number

  reportErrors: string[] = []

  isSaving: boolean = false

  isSendingErrorEmail: boolean = false

  isReimporting: boolean = false

  dashboard?: Dashboard

  dashboardByMonth: DashboardMatrix | undefined = undefined

  allStudents: Student[] = []

  rows: any[] = []

  selectedTab: StudentTab = StudentTab.All

  currentMonth: Month = defaultMonth

  // Number of "no report yet" polls remaining for the current institution.
  // When a user uploads from the external form, the ExcelReport row is created by a background
  // task — so navigating to the institution immediately after upload may briefly see no report.
  // Poll a few times before giving up.
  private noReportPollsRemaining: number = 0

  constructor(readonly owner: MainStore) {
    makeAutoObservable(this)

    reaction(
      () => this.owner.institutionStore.currentInstitution,
      currentInstitution => {
        // Reset the polling budget every time the user enters a new institution.
        this.noReportPollsRemaining = 10 // 10 × 3s = 30s
        this.getData(currentInstitution?.id)
      }
    )

    reaction(
      () => this.lastReport,
      lastReport => {
        if (lastReport) {
          this.noReportPollsRemaining = 0
          this.getStudentReports()
          // Legacy safety net: report stuck in New status (import never started) — kick it off.
          if (lastReport.statusId === 1)
            this.owner.institutionStore.importReport(lastReport.id)
          // Async import is running on the backend (status=InProgress=6); poll for completion.
          if (lastReport.statusId === 6) {
            setTimeout(() => {
              const month = this.currentMonth
              if (month) this.getLastReport(month.id, month.numericYear)
            }, 3000)
          }
        } else {
          this.studentReports = []
          // No report yet — the user may have just uploaded one via the external form and the
          // background task hasn't finished creating the ExcelReport row. Poll until it shows up
          // (or the budget runs out).
          if (this.noReportPollsRemaining > 0) {
            this.noReportPollsRemaining--
            const expectedInst =
              this.owner.institutionStore.currentInstitution?.id
            setTimeout(() => {
              if (
                this.owner.institutionStore.currentInstitution?.id ===
                expectedInst
              ) {
                const month = this.currentMonth
                if (month) this.getLastReport(month.id, month.numericYear)
              }
            }, 3000)
          }
        }
      }
    )

    reaction(
      () => this.owner.loginStore.isMsalAuth,
      isAuth => {
        if (isAuth) {
          this.getStudentStatuses()

          this.getlearningShifts()
          this.getStudentImportStatuses()
          this.setCurrentMonth(this.currentMonth)
        }
      }
    )
  }

  setSelectedTab = (tab: StudentTab) => {
    this.selectedTab = tab
  }

  setCurrentMonth = (month: Month) => {
    this.currentMonth = month
    this.getMonthlyData(month)
  }

  getData = (instutionId?: number) => {
    if (instutionId) {
      this.reportErrors = []
      this.getStudents()
      this.getRejects(instutionId)
      this.getRemovedReportRows(instutionId)
      this.getMonthlyChanges(instutionId)
      this.getDuplicates(this.currentMonth.id, this.currentMonth.numericYear)
      this.getLastReport(this.currentMonth.id, this.currentMonth.numericYear)
      this.getStudentsDashboard(instutionId)
      this.getMonthDashboard(instutionId)
    } else {
      this.students = []
      this.lastReport = undefined
    }
  }

  getMonthlyData = (month: Month) => {
    this.getStudents()
    this.getDuplicates(month.id, month.numericYear)
    this.getLastReport(month.id, month.numericYear)
  }

  getStudentsData = () => {
    this.getData(this.owner.institutionStore.currentInstitution?.id)
  }

  getLastUpdate = () => {
    if (this.students?.length > 0) {
      return format(new Date(this.students[0].updatedAt), 'dd/MM/yyyy')
    }
    return ''
  }

  setSelectedInstitutionId = (institutionId: number) => {
    this.selectedInstitutionId = institutionId
  }

  getStudentStatuses = () => {
    this.owner.loginStore
      .fetchWithUser(
        `${config.apiUrl}/CodeTables/${systemObjects.studentStatuses}`
      )
      .then((res: any) => {
        return res.json()
      })
      .then((data: any) => {
        if (data) {
          runInAction(() => {
            this.studentStatuses = data.map((i: any) => new CodeObject(i))
          })
        }
      })
      .catch((e: any) => {
        console.log('error:', e)
      })
  }

  getlearningShifts = () => {
    this.owner.loginStore
      .fetchWithUser(
        `${config.apiUrl}/CodeTables/${systemObjects.learningShifts}`
      )
      .then((res: any) => {
        return res.json()
      })
      .then((data: any) => {
        if (data) {
          runInAction(() => {
            this.learningShifts = data.map((i: any) => new CodeObject(i))
          })
        }
      })
      .catch((e: any) => {
        console.log('error:', e)
      })
  }

  getStudentImportStatuses = () => {
    this.owner.loginStore
      .fetchWithUser(
        `${config.apiUrl}/CodeTables/${systemObjects.studentImportStatuses}`
      )
      .then((res: any) => {
        return res.json()
      })
      .then((data: any) => {
        if (data) {
          runInAction(() => {
            this.studentImportStatuses = data.map((i: any) => new CodeObject(i))
          })
        }
      })
      .catch((e: any) => {
        console.log('error:', e)
      })
  }

  getStudentReports = () => {
    if (!this.lastReport?.id) return
    this.owner.loginStore
      .fetchWithUser(
        `${config.apiUrl}/StudentReports/${this.lastReport?.id}/students`
      )
      .then((res: any) => {
        return res.json()
      })
      .then((data: StudentReport[]) => {
        if (data) {
          runInAction(() => {
            this.studentReports = data.map(
              s =>
                new StudentReport(
                  s,
                  this.learningShifts!,
                  this.studentImportStatuses
                )
            )
          })
        }
      })
      .catch((e: any) => {
        console.log('error:', e)
      })
  }

  getLastReport = (month: number, year: number) => {
    const institutionId = this.owner.institutionStore.currentInstitution?.id

    if (!institutionId) {
      return
    }

    const params: any = {
      month,
      year
    }

    const url = new URL(
      `${config.apiUrl}/StudentReports/Institution/${institutionId}/last`
    )

    Object.keys(params).forEach(key =>
      url.searchParams.append(key, params[key])
    )

    runInAction(() => {
      this.isLoadingLastReport = true
    })
    this.owner.loginStore
      .fetchWithUser(url.toString())
      .then((res: any) => {
        if (res.status === 204) return null
        return res.json()
      })
      .then((data: Report | null) => {
        runInAction(() => {
          this.lastReport = data ? new Report(data) : undefined
          this.isLoadingLastReport = false
        })
      })
      .catch((e: any) => {
        console.log('error:', e)
        runInAction(() => {
          this.isLoadingLastReport = false
        })
      })
  }

  getAllStudents = () => {
    this.owner.loginStore
      .fetchWithUser(`${config.apiUrl}/Students`)
      .then((res: any) => res.json())
      .then((data: any[]) => {
        if (data) {
          runInAction(() => {
            this.allStudents = data.map(
              s => new Student(s, this.learningShifts ?? [])
            )
          })
        }
      })
      .catch((e: any) => console.error('getAllStudents error:', e))
  }

  getStudents = () => {
    const institutionId = this.owner.institutionStore.currentInstitution?.id

    if (!institutionId) {
      return
    }

    const url = `${config.apiUrl}/Students/Institution/${institutionId}`

    this.owner.loginStore
      .fetchWithUser(url)
      .then((res: any) => {
        return res.json()
      })
      .then((data: Student[]) => {
        if (data) {
          runInAction(() => {
            const ss = data.map(s => new Student(s, this.learningShifts ?? []))
            this.students = ss
            if (this.selectedTab === StudentTab.All) {
              this.rows = ss
            } else if (this.selectedTab === StudentTab.Approved) {
              this.rows = ss.filter(
                s => !s.isReject && (s.isNeedPay || s.statusId === 1)
              )
            }
          })
        }
      })
      .catch((e: any) => {
        console.log('error:', e)
      })
  }

  getDuplicates = (month: number, year: number) => {
    const institutionId = this.owner.institutionStore.currentInstitution?.id

    if (!institutionId) {
      return
    }

    const params: any = {
      month,
      year
    }

    const url = new URL(
      `${config.apiUrl}/Students/Institution/${institutionId}/duplicates`
    )

    Object.keys(params).forEach(key =>
      url.searchParams.append(key, params[key])
    )

    this.owner.loginStore
      .fetchWithUser(url.toString())
      .then((res: any) => {
        return res.json()
      })
      .then((data: Student[]) => {
        if (data) {
          runInAction(() => {
            const ss = data.map(s => new Student(s, this.learningShifts ?? []))
            this.duplicates = ss
            if (this.selectedTab === StudentTab.Duplicate) {
              this.rows = ss
            }
          })
        }
      })
      .catch((e: any) => {
        console.log('error:', e)
      })
  }

  getRejects = (institutionId?: number) => {
    if (!institutionId) this.students = []
    else {
      this.owner.loginStore
        .fetchWithUser(
          `${config.apiUrl}/Students/Institution/${institutionId}/rejections`
        )
        .then((res: any) => {
          return res.json()
        })
        .then((data: StudentRejection[]) => {
          if (data) {
            runInAction(() => {
              const ss = data.map(s => new StudentRejection(s))
              this.rejects = ss
              if (this.selectedTab === StudentTab.Rejected) {
                this.rows = ss
              }
            })
          }
        })
        .catch((e: any) => {
          console.log('error: ', e)
        })
    }
  }

  setFailedStatus = () => {
    this.lastReport?.setStatusId(3)
  }

  setIsSaving = (isSaving: boolean) => {
    this.isSaving = isSaving
  }

  approveReport = async () => {
    try {
      this.setIsSaving(true)
      this.reportErrors = []
      const response = await this.owner.loginStore.fetchWithUser(
        `${config.apiUrl}/StudentReports/${this.lastReport?.id}/accept`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      )
      const data = await response.json()
      const errors = data as string[]
      if (errors.length === 0) {
        this.lastReport?.setStatusId(4)
        this.getStudents()
        this.getRejects(this.owner.institutionStore.currentInstitution?.id)
        this.getDuplicates(this.currentMonth.id, this.currentMonth.numericYear)
      } else {
        this.lastReport?.setStatusId(5)
        this.reportErrors = errors
      }
      this.setIsSaving(false)
    } catch (error) {
      this.owner.setMessage('התהליך נכשל, נסה שוב.')
    }
  }

  // Rows pulled out of the institution's reports (IsRemoved) — the "מחוקים" tab. Stays available
  // after the report was approved, so a removed row can still be fixed and restored.
  // Month-by-month change log for the institution (joined / left / changed / active count).
  monthlyChanges: StudentMonthlyChange[] = []

  loadingMonthlyChanges: boolean = false

  removedReportRows: StudentReport[] = []

  getMonthlyChanges = (institutionId?: number) => {
    const id =
      institutionId ?? this.owner.institutionStore.currentInstitution?.id
    if (!id) {
      this.monthlyChanges = []
      return
    }
    runInAction(() => {
      this.loadingMonthlyChanges = true
    })
    this.owner.loginStore
      .fetchWithUser(
        `${config.apiUrl}/Students/Institution/${id}/monthly-changes`
      )
      .then((res: any) => res.json())
      .then((data: any[]) => {
        runInAction(() => {
          this.monthlyChanges = (data ?? []).map(
            m => new StudentMonthlyChange(m)
          )
        })
      })
      .catch((e: any) => console.log('getMonthlyChanges error:', e))
      .finally(() => {
        runInAction(() => {
          this.loadingMonthlyChanges = false
        })
      })
  }

  getRemovedReportRows = (institutionId?: number) => {
    const id =
      institutionId ?? this.owner.institutionStore.currentInstitution?.id
    if (!id) {
      this.removedReportRows = []
      return
    }
    this.owner.loginStore
      .fetchWithUser(
        `${config.apiUrl}/StudentReports/Institution/${id}/removed-rows`
      )
      .then((res: any) => res.json())
      .then((data: any[]) => {
        runInAction(() => {
          const rows = (data ?? []).map(
            r =>
              new StudentReport(
                r,
                this.learningShifts ?? [],
                this.studentImportStatuses
              )
          )
          this.removedReportRows = rows
          if (this.selectedTab === StudentTab.Removed) this.rows = rows
        })
      })
      .catch((e: any) => console.log('getRemovedReportRows error:', e))
  }

  // Pull a blocking row out of the report (isRemoved=true) or put it back (false). The backend
  // re-validates the report, so removing a blocking row is what actually unblocks approval.
  setReportRowRemoved = async (
    row: StudentReport,
    isRemoved: boolean
  ): Promise<boolean> => {
    const reportId = row.excelReportId ?? this.lastReport?.id
    if (!reportId || !row.id) return false
    try {
      const response = await this.owner.loginStore.fetchWithUser(
        `${config.apiUrl}/StudentReports/${reportId}/students/${row.id}/${
          isRemoved ? 'remove' : 'restore'
        }`,
        {method: 'PUT', headers: {'Content-Type': 'application/json'}}
      )
      // 409 = the server refused to restore a row that still has blocking errors, and tells us which.
      if (response.status === 409) {
        const reasons = await response.json().catch(() => [])
        const list: string[] = Array.isArray(reasons) ? reasons : []
        this.owner.setMessage(
          list.length > 0
            ? `לא ניתן להחזיר את התלמיד עד לתיקון: ${list.join(' | ')}`
            : 'לא ניתן להחזיר את התלמיד — יש לתקן קודם את הפרטים השגויים.',
          'error'
        )
        return false
      }
      if (!response.ok) throw new Error()
      const data = await response.json()
      const updated = new StudentReport(
        data,
        this.learningShifts ?? [],
        this.studentImportStatuses
      )
      runInAction(() => {
        // Keep the report grid in sync when the row belongs to the report being reviewed.
        this.studentReports = this.studentReports.map(r =>
          r.id === updated.id ? updated : r
        )
        // A blocking row was pulled out / put back — the previous approval errors no longer apply.
        this.reportErrors = []
      })
      // Re-validation on the server can change OTHER rows too — pulling one of two rows sharing a
      // ת"ז out of the report clears the duplicate flag on the one that stays. Refetch the whole
      // report so every row's statuses match the server, not just the row we acted on.
      this.getStudentReports()
      this.getRemovedReportRows()
      // Restoring into an already-approved report creates the student server-side.
      if (!isRemoved) this.getStudents()
      return true
    } catch (e) {
      console.log('setReportRowRemoved error:', e)
      this.owner.setMessage('הפעולה נכשלה, נסה שוב.')
      return false
    }
  }

  // Re-parse the currently-loaded (FailedLoaded=3) report from its stored file, in place — no
  // download/upload. importReport marks the report InProgress (6) and refreshes lastReport; the
  // existing lastReport reaction then polls until it resolves to SuccessLoaded (2 → the editable grid
  // opens automatically) or FailedLoaded (3 → stays on the failed screen).
  reimportCurrentReport = async () => {
    const reportId = this.lastReport?.id
    if (!reportId) return
    runInAction(() => {
      this.isReimporting = true
    })
    try {
      await this.owner.institutionStore.importReport(reportId)
    } catch (e) {
      console.log('reimportCurrentReport error:', e)
      this.owner.setMessage('טעינת הדו"ח מחדש נכשלה, נסה שוב.')
    } finally {
      runInAction(() => {
        this.isReimporting = false
      })
    }
  }

  // Inline-edit a single report row before approval (no re-upload). PUTs the edited row, gets back the
  // row with its re-computed statuses, and replaces it in studentReports so the grid + status chips
  // (and the approval-block check, which reads statusesList) refresh immediately.
  updateReportRow = async (
    row: StudentReport
  ): Promise<StudentReport | null> => {
    // Prefer the row's own report: the "removed" list can hold rows from an older report, not just
    // from the one currently being reviewed.
    const reportId = row.excelReportId ?? this.lastReport?.id
    if (!reportId || !row.id) return null
    try {
      const payload = {
        Id: row.id,
        FirstName: row.firstName ?? '',
        LastName: row.lastName ?? '',
        IndificationNo: row.indificationNo ?? '',
        Passport: row.passport ?? null,
        Street: row.street ?? '',
        HouseNumber: row.houseNumber ?? '',
        City: row.city ?? '',
        Phone: row.phone ?? '',
        LearningShiftId: row.learningShiftId ?? 0,
        LearnLocation: row.learnLocation ?? '',
        Scholarship: Number(row.scholarship) || 0,
        Framework: row.framework ?? null,
        Notes: row.notes ?? null,
        ExcelReportId: row.excelReportId ?? reportId
      }
      const response = await this.owner.loginStore.fetchWithUser(
        `${config.apiUrl}/StudentReports/${reportId}/students/${row.id}`,
        {
          method: 'PUT',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify(payload)
        }
      )
      if (!response.ok) return null
      const data = await response.json()
      const updated = new StudentReport(
        data,
        this.learningShifts!,
        this.studentImportStatuses
      )
      runInAction(() => {
        this.studentReports = this.studentReports.map(r =>
          r.id === updated.id ? updated : r
        )
        // Keep the "removed" list in sync too — it's where a removed row gets fixed before restoring.
        this.removedReportRows = this.removedReportRows.map(r =>
          r.id === updated.id ? updated : r
        )
        if (this.selectedTab === StudentTab.Removed)
          this.rows = [...this.removedReportRows]
      })
      // An edit can clear (or raise) flags on OTHER rows — fixing one of two duplicate ת"ז values
      // resolves the other row too — so resync the whole report, not just the edited row.
      this.getStudentReports()
      return updated
    } catch (e) {
      console.log('updateReportRow error:', e)
      return null
    }
  }

  // Emails the institution about the failed students-report upload (attaching the original template +
  // the file they sent), invites them to re-upload via the update form, and flags the institution in the
  // DB as awaiting a new file. The errored file is attached server-side from the last report's document.
  // Human-readable list of the problems currently visible in the report: the blocking approval errors
  // when there are any, otherwise a per-row summary ("שורה 4 — ת.ז 123: ערך לא תקין"). Lets the
  // reviewer email the institution about an invalid report even when approval wasn't attempted/blocked.
  buildReportErrorSummary = (): string[] => {
    if (this.reportErrors.length > 0) return [...this.reportErrors]
    const active = this.studentReports.filter(r => !r.isRemoved)
    return active
      .map((r, i) => {
        const problems = (r.studentImportStatuses ?? [])
          .map(s => s.description)
          .filter(Boolean)
        if (problems.length === 0) return null
        const who =
          [r.firstName, r.lastName].filter(Boolean).join(' ').trim() ||
          r.indificationNo ||
          r.passport ||
          ''
        return `שורה ${i + 1}${who ? ` — ${who}` : ''}: ${problems.join(', ')}`
      })
      .filter((s): s is string => s !== null)
  }

  sendReportErrorEmail = async (
    additionalText: string,
    errorsOverride?: string[]
  ): Promise<boolean> => {
    const institutionId = this.owner.institutionStore.currentInstitution?.id
    if (!institutionId) return false
    runInAction(() => {
      this.isSendingErrorEmail = true
    })
    try {
      const fd = new FormData()
      fd.append('documentId', String(this.lastReport?.document?.id ?? 0))
      fd.append('additionalText', additionalText ?? '')
      fd.append('errors', (errorsOverride ?? this.reportErrors).join('\n'))
      const res = await this.owner.loginStore.fetchWithUser(
        `${config.apiUrl}/Institutions/${institutionId}/SendStudentReportErrorEmail`,
        {method: 'POST', body: fd}
      )
      if (res.status === 200) {
        this.owner.setMessage(
          'המייל נשלח למוסד, וסומן שממתינים לקובץ תלמידים חדש.',
          'success'
        )
        return true
      }
      this.owner.setMessage('שליחת המייל נכשלה, נסה שוב.', 'error')
      return false
    } catch (e) {
      this.owner.setMessage('שליחת המייל נכשלה, נסה שוב.', 'error')
      return false
    } finally {
      runInAction(() => {
        this.isSendingErrorEmail = false
      })
    }
  }

  getStudentsDashboard = async (institutionId: number) => {
    this.owner.loginStore
      .fetchWithUser(
        `${config.apiUrl}/Students/Institution/${institutionId}/dashboard`
      )
      .then((res: any) => {
        return res.json()
      })
      .then((data: any) => {
        if (data) {
          runInAction(() => {
            this.dashboard = new Dashboard(data)
          })
        }
      })
      .catch((e: any) => {
        console.log('error:', e)
      })
  }

  getMonthDashboard = async (institutionId: number) => {
    this.owner.loginStore
      .fetchWithUser(
        `${config.apiUrl}/Students/Institution/${institutionId}/monthDashboard`
      )
      .then((res: any) => {
        return res.json()
      })
      .then((data: any) => {
        if (data) {
          runInAction(() => {
            this.dashboardByMonth = {}
            data.forEach((d: any) => {
              if (!this.dashboardByMonth![d.Year]) {
                this.dashboardByMonth![d.Year] = {}
              }
              this.dashboardByMonth![d.Year][d.Month] = new Dashboard(d)
            })
          })
        }
      })
      .catch((e: any) => {
        console.log('error:', e)
      })
  }

  rejectStudent = async (
    student: Student,
    comment: string,
    startMonth?: number,
    startNumericYear?: number,
    endMonth?: number,
    endNumericYear?: number
  ) => {
    student.setReject(!student.isReject)
    try {
      const response = await this.owner.loginStore.fetchWithUser(
        `${config.apiUrl}/Students/rejection`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            FirstName: student.firstName,
            LastName: student.lastName,
            IndificationNo: student.indificationNo,
            Passport: student.passport ?? '',
            InstitutionId: student.institutionId,
            Comment: comment,
            StartMonth: startMonth ?? null,
            StartNumericYear: startNumericYear ?? null,
            EndMonth: endMonth ?? null,
            EndNumericYear: endNumericYear ?? null
          })
        }
      )
      const data = await response.json()
      if (data === -1) {
        student.setReject(!student.isReject)
        this.owner.setMessage('התלמיד כבר נדחה.')
        return
      }
      if (typeof data === 'number' && data > 0) {
        this.rejects.push(new StudentRejection({}))
        this.getStudents()
        this.owner.setMessage('התלמיד נדחה בהצלחה.')
        return
      }
      console.error('rejectStudent unexpected response', data)
      student.setReject(!student.isReject)
      this.owner.setMessage('התהליך נכשל, נסה שוב.')
    } catch (error) {
      console.error('rejectStudent failed', error)
      student.setReject(!student.isReject)
      this.owner.setMessage('התהליך נכשל, נסה שוב.')
    }
  }

  cancelStudentRejection = async (studentRejectionId: number) => {
    const index = this.rejects.findIndex(item => item.id === studentRejectionId)
    let deleteObj: StudentRejection[] = []
    if (index !== -1) {
      deleteObj = this.rejects.splice(index, 1)
      this.rows = [...this.rejects]
    }
    try {
      const response = await this.owner.loginStore.fetchWithUser(
        `${config.apiUrl}/Students/rejection/${studentRejectionId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      )
      const data = await response.json()
      if (data === -1) throw new Error()
      // Backend cleared the student's IsReject flag — refresh the local student list
      // so the 'מאושר' tab filter (!s.isReject) sees the new value.
      this.getStudents()
    } catch (error) {
      if (deleteObj.length) this.rejects.splice(index, 0, deleteObj[0])
      this.owner.setMessage('התהליך נכשל, נסה שוב.')
    }
  }

  updateRejection = async (
    rejectionId: number,
    comment: string,
    startMonth?: number,
    startNumericYear?: number,
    endMonth?: number,
    endNumericYear?: number
  ) => {
    try {
      const rejection = this.rejects.find(r => r.id === rejectionId)
      if (!rejection) {
        throw new Error('Rejection not found')
      }

      const response = await this.owner.loginStore.fetchWithUser(
        `${config.apiUrl}/Students/updateRejection`,
        {
          method: 'patch',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            Id: rejectionId,
            FirstName: rejection.firstName,
            LastName: rejection.lastName,
            IndificationNo: rejection.indificationNo,
            Passport: rejection.passport ?? '',
            InstitutionId: this.owner.institutionStore.currentInstitution?.id,
            Comment: comment,
            StartMonth: startMonth ?? null,
            StartNumericYear: startNumericYear ?? null,
            EndMonth: endMonth ?? null,
            EndNumericYear: endNumericYear ?? null
          })
        }
      )

      const data = await response.json()
      if (data) {
        runInAction(() => {
          if (rejection) {
            rejection.comment = comment
            rejection.startMonth = startMonth || 0
            rejection.startNumericYear = startNumericYear || 0
            rejection.endMonth = endMonth || 0
            rejection.endNumericYear = endNumericYear || 0
          }
        })
        this.owner.setMessage('הדחיה עודכנה בהצלחה.')
        this.getStudents()
      } else {
        throw new Error()
      }
    } catch (error) {
      this.owner.setMessage('התהליך נכשל, נסה שוב.')
    }
  }

  editStudentEffectiveFrom = async (
    studentId: number,
    payload: {
      shiftId: number
      scholarship: number
      isActive: boolean
      effectiveFromMonth: number
      effectiveFromNumericYear: number
    }
  ): Promise<{success: boolean; reason?: string}> => {
    try {
      const response = await this.owner.loginStore.fetchWithUser(
        `${config.apiUrl}/Students/${studentId}/edit-effective-from`,
        {
          method: 'PUT',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({
            ShiftId: payload.shiftId,
            Scholarship: payload.scholarship,
            IsActive: payload.isActive,
            EffectiveFromMonth: payload.effectiveFromMonth,
            EffectiveFromNumericYear: payload.effectiveFromNumericYear
          })
        }
      )
      if (!response.ok) return {success: false, reason: 'התהליך נכשל'}
      return {success: true}
    } catch (error: any) {
      if (error?.status === 409) {
        return {
          success: false,
          reason: 'לא ניתן לערוך תלמיד לחודש שכבר נסגר במוסד זה'
        }
      }
      return {success: false, reason: 'התהליך נכשל'}
    }
  }

  // Spell out WHICH month blocks and which one would be accepted — "the month is closed" alone
  // leaves the user guessing at a valid month.
  private buildClosedMonthReason = (info: any): string => {
    const cm = info?.ClosedThroughMonth
    const cy = info?.ClosedThroughNumericYear
    const am = info?.EarliestAllowedMonth
    const ay = info?.EarliestAllowedNumericYear
    if (!cm || !cy)
      return 'לא ניתן לקבוע חודש התחלה בחודש שכבר נסגר לתשלום במוסד זה'
    const closed = `${getMonthName(cm, cy)} ${getYearText(cy)}`
    const allowed =
      am && ay ? `${getMonthName(am, ay)} ${getYearText(ay)}` : null
    return (
      `לא ניתן — במוסד זה כבר שולמו/יוצאו לדוח תשלומים עד ${closed}.` +
      (allowed ? ` ניתן לקבוע חודש התחלה מ${allowed} ואילך.` : '')
    )
  }

  // Move the leaving month of the students a report removed in one month to another month. A
  // wrongly reported roster is corrected by the next report, but the students it dropped are
  // stamped as having left in the UPLOAD month and keep counting for every month in between;
  // moving their leaving month back to their enrolment month takes them out of every month.
  // Months already paid do not block it (unlike the enrolled-from actions): recorded payments keep
  // their amounts, so the dialog warns instead of the API refusing.
  bulkUpdateLeftAt = async (
    from: {month: number; year: number},
    to: {month: number; year: number}
  ): Promise<{
    success: boolean
    updated?: number
    clamped?: number
    reason?: string
  }> => {
    const institutionId = this.owner.institutionStore.currentInstitution?.id
    if (!institutionId) return {success: false, reason: 'לא נבחר מוסד'}
    try {
      const response = await this.owner.loginStore.fetchWithUser(
        `${config.apiUrl}/Students/Institution/${institutionId}/left-at` +
          `?fromMonth=${from.month}&fromYear=${from.year}&month=${to.month}&year=${to.year}`,
        {method: 'PUT', headers: {'Content-Type': 'application/json'}}
      )
      const data = await response.json()
      // The active list does not change (these students had already left); what changes is the
      // month-by-month log and the per-month figures.
      this.getMonthlyChanges(institutionId)
      this.getStudentsDashboard(institutionId)
      this.getMonthDashboard(institutionId)
      return {
        success: true,
        updated: data?.Updated ?? 0,
        clamped: data?.Clamped ?? 0
      }
    } catch {
      return {success: false, reason: 'הפעולה נכשלה'}
    }
  }

  // Back-date the enrolment start of every active student in the institution. The first file an
  // institution sends is filed under the month it was uploaded, so the whole cohort usually needs
  // moving to the month they actually started.
  bulkUpdateEnrolledFrom = async (
    month: number,
    year: number
  ): Promise<{success: boolean; updated?: number; reason?: string}> => {
    const institutionId = this.owner.institutionStore.currentInstitution?.id
    if (!institutionId) return {success: false, reason: 'לא נבחר מוסד'}
    try {
      const response = await this.owner.loginStore.fetchWithUser(
        `${config.apiUrl}/Students/Institution/${institutionId}/enrolled-from?month=${month}&year=${year}`,
        {method: 'PUT', headers: {'Content-Type': 'application/json'}}
      )
      const data = await response.json()
      this.getStudents()
      return {success: true, updated: data?.Updated ?? 0}
    } catch (err: any) {
      // fetchWithUser rejects with the Response itself on a non-2xx, so the refusal details (which
      // month is closed, and the earliest one that would be accepted) are read from here — not from
      // a status check on a resolved response, which never runs.
      if (err?.status === 409) {
        const info = await err.json?.().catch(() => null)
        return {success: false, reason: this.buildClosedMonthReason(info)}
      }
      return {success: false, reason: 'הפעולה נכשלה'}
    }
  }

  addStudent = async (payload: {
    firstName: string
    lastName: string
    indificationNo: string
    passport?: string
    phone?: string
    street?: string
    houseNumber?: string
    city?: string
    shiftId: number
    scholarship: number
    framework?: string
    enrolledFromMonth: number
    enrolledFromNumericYear: number
  }): Promise<{success: boolean; reason?: string}> => {
    const institutionId = this.owner.institutionStore.currentInstitution?.id
    if (!institutionId) return {success: false, reason: 'לא נבחר מוסד'}
    try {
      const response = await this.owner.loginStore.fetchWithUser(
        `${config.apiUrl}/Students`,
        {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({
            FirstName: payload.firstName,
            LastName: payload.lastName,
            IndificationNo: payload.indificationNo,
            Passport: payload.passport ?? '',
            Phone: payload.phone ?? '',
            Street: payload.street ?? '',
            HouseNumber: payload.houseNumber ?? '',
            City: payload.city ?? '',
            InstitutionId: institutionId,
            ShiftId: payload.shiftId,
            Scholarship: payload.scholarship,
            Framework: payload.framework ?? '',
            EnrolledFromMonth: payload.enrolledFromMonth,
            EnrolledFromNumericYear: payload.enrolledFromNumericYear
          })
        }
      )
      if (response.status === 409) {
        const text = await response.text().catch(() => '')
        return {
          success: false,
          reason: text || 'תלמיד עם ת.ז זו כבר קיים במוסד'
        }
      }
      if (!response.ok) return {success: false, reason: 'התהליך נכשל'}
      this.getStudents()
      return {success: true}
    } catch (err) {
      if (err instanceof Response && err.status === 409) {
        const text = await err.text().catch(() => '')
        return {
          success: false,
          reason: text || 'תלמיד עם ת.ז זו כבר קיים במוסד'
        }
      }
      return {success: false, reason: 'התהליך נכשל'}
    }
  }

  softDeleteStudent = async (
    studentId: number,
    leftAtMonth: number,
    leftAtYear: number
  ): Promise<{success: boolean; reason?: string}> => {
    try {
      const response = await this.owner.loginStore.fetchWithUser(
        `${config.apiUrl}/Students/${studentId}?month=${leftAtMonth}&year=${leftAtYear}`,
        {method: 'DELETE'}
      )
      if (!response.ok) return {success: false, reason: 'התהליך נכשל'}
      this.getStudents()
      return {success: true}
    } catch {
      return {success: false, reason: 'התהליך נכשל'}
    }
  }

  updateStudentStatus = async (
    student: Student,
    statusId: number | undefined
  ) => {
    const prevStatus = student.statusId
    if (prevStatus === statusId) return
    student.setStatus(statusId)
    try {
      const response = await this.owner.loginStore.fetchWithUser(
        `${config.apiUrl}/Students/${student.id}/status`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(statusId)
        }
      )
      const data = await response.json()
      if (data) {
        const i = this.students.findIndex(s => s.id === student.id)
        if (i !== -1) {
          if (statusId === 1) {
            const help = [...this.students]
            help[i].isNeedPay = true
            this.students = help
          }
          if (prevStatus === 1) {
            const help = [...this.students]
            help[i].isNeedPay = false
            this.students = help
          }
          if (statusId === 2) {
            if (this.students.find(s => s.id === student.id))
              this.rejects.push(new StudentRejection({}))
          }
        }
      } else {
        student.setStatus(prevStatus)
        throw new Error()
      }
    } catch (error) {
      this.owner.setMessage('התהליך נכשל, נסה שוב.')
    }
  }

  updateStudentComment = async (student: Student, comment: string) => {
    const prevComment = student.studentDatotComment || ''
    if (prevComment === comment) return

    // Save the raw comment with line breaks
    student.setStudentDatotComment(comment)
    try {
      const response = await this.owner.loginStore.fetchWithUser(
        `${config.apiUrl}/Students/updateStudentDatotComment`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            InstitutionId: student.institutionId,
            IndificationNo: student.indificationNo,
            Passport: student.passport ?? '',
            Comment: comment
          })
        }
      )
      const data = await response.json()
      if (data) {
        runInAction(() => {
          const studentRef = this.students.find(s => s.id === student.id)
          if (studentRef) {
            studentRef.setStudentDatotComment(comment)
            studentRef.setStudentDatotCommentDate(new Date())
          }
        })
      } else {
        runInAction(() => {
          student.setStudentDatotComment(prevComment)
        })
        throw new Error()
      }
    } catch (error) {
      this.owner.setMessage('התהליך נכשל, נסה שוב.')
    }
  }

  setRows = (rows: any[]) => {
    this.rows = rows
  }

  changeTab = async (tab: StudentTab) => {
    const institutionId = this.owner.institutionStore.currentInstitution?.id

    if (!institutionId) {
      return
    }

    const month = this.currentMonth.id
    const year = this.currentMonth.numericYear
    this.rows = []
    this.selectedTab = tab
    switch (tab) {
      case StudentTab.All:
        this.getStudents()
        break
      case StudentTab.Approved:
        this.getStudents()
        break
      case StudentTab.Duplicate:
        this.getDuplicates(month!, year!)
        break
      case StudentTab.Rejected:
        this.getRejects(institutionId)
        break
      case StudentTab.Removed:
        this.getRemovedReportRows(institutionId)
        break
      default:
        break
    }
  }
}
